import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Pyng" },
      { name: "description", content: "How Pyng handles your data — privacy-first by design." },
      { property: "og:title", content: "Privacy Policy — Pyng" },
      { property: "og:description", content: "How Pyng handles your data — privacy-first by design." },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" lastUpdated="May 2026">
      <p>Pyng exists to help you stay in touch with the people who matter. That only works if you trust us with the most personal data you have — your relationships. We take that seriously.</p>

      <h2>What we collect</h2>
      <ul>
        <li><strong>Account data:</strong> your name, email, and authentication info.</li>
        <li><strong>Contacts you add:</strong> names, emails, phone numbers, notes, and the context you record about your relationships.</li>
        <li><strong>Voice memos and transcripts:</strong> stored encrypted, used only to extract facts you've chosen to remember.</li>
        <li><strong>Calendar data (Live Context):</strong> only when you explicitly connect Google Calendar, and only to prep you before meetings and create post-meeting hooks.</li>
        <li><strong>Email metadata only:</strong> when you connect Gmail, Pyng reads message <em>headers</em> — who, when, and the subject line — for the people already in your orbit. We never read, store, or process the body of an email, and we never send email on your behalf without you pressing send. It exists for one reason: to show your real last touch and notice when someone replied.</li>
        <li><strong>Minimal usage data:</strong> errors and basic interaction metrics so we can fix bugs.</li>
      </ul>

      <h2>What we don't do</h2>
      <ul>
        <li>We don't sell your data. Not now, not ever.</li>
        <li>We don't share your contacts or memories with advertisers.</li>
        <li>We don't train public AI models on your private content.</li>
        <li>We don't read your messages or notes for any purpose other than running the features you use.</li>
      </ul>

      <h2>How AI is used</h2>
      <p>When Pyng drafts a message, summarizes a relationship, or extracts facts from a voice note, it sends the minimum necessary content to our AI provider. Those requests are not used to train external models. Public context (LinkedIn, company sites) is fetched on your behalf only for contacts you've added.</p>

      <h2>Your control</h2>
      <ul>
        <li>Export everything you've stored at any time.</li>
        <li>Delete a single contact, a single memory, or your entire account — gone means gone.</li>
        <li>Disconnect calendar or any integration in one tap.</li>
      </ul>

      <h2>Where data lives</h2>
      <p>Data is stored on encrypted infrastructure in the EU/US. Backups are encrypted at rest. Access is limited to a small team and audited.</p>

      <h2>Who can see it</h2>
      <p>
        The people you watch and everything you write about them are visible only to your signed-in
        account. No other subscriber can see who is on your list. Cross-account access is blocked at the
        database layer. A small founding team can reach production infrastructure to run the service and
        investigate incidents; that access is logged. Nothing you store is sold, shared with other
        subscribers, or used to train third-party AI models. Pyng is encrypted in transit and at rest —
        it is not end-to-end encrypted, because our servers process your content to produce signals and
        drafts.
      </p>

      <h2>Legal requests</h2>
      <p>
        We disclose data only where legally compelled. If we receive a subpoena, court order, or
        regulatory demand about your account, we will notify you unless prohibited by law, push back on
        overbroad requests, and disclose no more than required.
      </p>

      <h2>Contact</h2>
      <p>Questions about your data? Use the Contact link in the footer or email <a href="mailto:privacy@pyng.co">privacy@pyng.co</a>.</p>
    </LegalPage>
  );
}
